"use client";

import { useSearchParams } from "next/navigation";

export interface IUserQueryParams {
  chainQueryParamsToUrl(url: string, params?: Record<string, string | undefined>): string;
  queryParamsForInterpolation(): Record<string, string>
}

const addParamsToTargetUrl = (targetUrl: string, key: string, value: string | undefined, paramStrings: string[]) => {
  if (!!value && key !== "slug" && !targetUrl.includes(`${key}=`)) {
    const paramKeyValue = `${key}=${value}`;
    if (!paramStrings.includes(paramKeyValue)) {
      paramStrings.push(`${key}=${value}`);
    }
  }
};

export function useQueryParams(): IUserQueryParams {
  const currentQueryParams = useSearchParams();

  const chainQueryParamsToUrl = (targetUrl: string, newParams?: Record<string, string | undefined>) => {
    if (!currentQueryParams && !newParams) { return targetUrl; }
    const paramStrings: string[] = [];
    let paramString = "";
    const hash = typeof window !== "undefined" && window.location.hash ? `${window.location.hash}` : "";
    const hashLessTargetUrl = targetUrl.replace(hash, "");
    const paramsPrefix = hashLessTargetUrl.includes("?") ? "&" : "?";

    if (newParams) {
      Object.entries(newParams).forEach(([ key, value ]) => {
        addParamsToTargetUrl(hashLessTargetUrl, key, value, paramStrings);
      });
      paramString = paramStrings.join("&");
    } else {
      const currentParams = new URLSearchParams(currentQueryParams.toString());
      const targetParams = new URLSearchParams(hashLessTargetUrl.split("?")[1] || "");

      currentParams.forEach((value, key) => {
        // Only copy over query params that aren’t already defined on the target URL
        if (!targetParams.has(key)) {
          paramStrings.push(`${key}=${value}`);
        }
      });
      paramString = paramStrings.join("&");
    }

    if (!paramString.length) { return hashLessTargetUrl; }

    return `${hashLessTargetUrl}${paramsPrefix}${encodeURI(paramString)}${hash}`;
  };

  const queryParamsForInterpolation = () => {
    const queryValues: Record<string, string> = {};
    Object.entries(currentQueryParams).forEach(([ key, value ]) => {
      if (value) {
        queryValues[`$${key}`] = value?.toString();
      }
    });
    return queryValues;
  };

  return {
    chainQueryParamsToUrl,
    queryParamsForInterpolation,
  };
}
